import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, MapPin, Check, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui';

interface ReleaseLocation {
  id: string;
  name: string;
  x: number;
  y: number;
  waterQuality: number; // 0-100
  foodAvailability: number;
  predatorRisk: number;
  humanActivity: number;
  suitable: boolean;
}

interface ReleasePrepGameProps {
  onComplete: (score: number, stars: number) => void;
  onBack: () => void;
  speciesId: string;
  otterName: string;
}

function generateLocations(): ReleaseLocation[] {
  const locations: ReleaseLocation[] = [
    {
      id: 'loc1',
      name: 'Pristine Cove',
      x: 25,
      y: 30,
      waterQuality: 95,
      foodAvailability: 85,
      predatorRisk: 15,
      humanActivity: 10,
      suitable: true,
    },
    {
      id: 'loc2',
      name: 'Industrial Bay',
      x: 70,
      y: 25,
      waterQuality: 30,
      foodAvailability: 40,
      predatorRisk: 25,
      humanActivity: 90,
      suitable: false,
    },
    {
      id: 'loc3',
      name: 'Fishing Village',
      x: 55,
      y: 60,
      waterQuality: 60,
      foodAvailability: 70,
      predatorRisk: 20,
      humanActivity: 75,
      suitable: false,
    },
    {
      id: 'loc4',
      name: 'Protected Reserve',
      x: 30,
      y: 70,
      waterQuality: 90,
      foodAvailability: 90,
      predatorRisk: 10,
      humanActivity: 5,
      suitable: true,
    },
    {
      id: 'loc5',
      name: 'Rocky Outcrop',
      x: 80,
      y: 55,
      waterQuality: 85,
      foodAvailability: 50,
      predatorRisk: 40,
      humanActivity: 20,
      suitable: false,
    },
    {
      id: 'loc6',
      name: 'Kelp Forest',
      x: 15,
      y: 50,
      waterQuality: 88,
      foodAvailability: 95,
      predatorRisk: 25,
      humanActivity: 15,
      suitable: true,
    },
  ];

  return locations;
}

function getOverallScore(loc: ReleaseLocation): number {
  return Math.round(
    (loc.waterQuality * 0.3) +
    (loc.foodAvailability * 0.3) +
    ((100 - loc.predatorRisk) * 0.2) +
    ((100 - loc.humanActivity) * 0.2)
  );
}

export function ReleasePrepGame({ onComplete, onBack, speciesId, otterName }: ReleasePrepGameProps) {
  const [locations] = useState<ReleaseLocation[]>(() => generateLocations());
  const [selectedLocation, setSelectedLocation] = useState<ReleaseLocation | null>(null);
  const [checkedLocations, setCheckedLocations] = useState<Set<string>>(new Set());
  const [finalChoice, setFinalChoice] = useState<ReleaseLocation | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  const handleLocationClick = useCallback((location: ReleaseLocation) => {
    if (finalChoice) return;

    setSelectedLocation(location);
    setCheckedLocations(prev => new Set([...prev, location.id]));
  }, [finalChoice]);

  const handleConfirmRelease = useCallback(() => {
    if (!selectedLocation) return;

    setFinalChoice(selectedLocation);
    setIsComplete(true);

    const isCorrect = selectedLocation.suitable;
    const thoroughness = checkedLocations.size;

    let stars = 0;
    if (isCorrect) {
      stars = thoroughness >= 4 ? 3 : thoroughness >= 2 ? 2 : 1;
    }

    const score = isCorrect ? getOverallScore(selectedLocation) + thoroughness * 10 : 0;
    setTimeout(() => onComplete(score, stars), 2000);
  }, [selectedLocation, checkedLocations, onComplete]);

  const StatBar = ({ label, value, color }: { label: string; value: number; color: string }) => (
    <div className="flex items-center gap-2">
      <span className="text-white/60 text-xs w-24">{label}</span>
      <div className="flex-1 h-2 bg-black/30 rounded-full overflow-hidden">
        <motion.div
          className={`h-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${value}%` }}
        />
      </div>
      <span className="text-white text-xs w-8 text-right">{value}%</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gradient-to-b from-teal-900 to-teal-950 flex flex-col">
      {/* Header */}
      <div className="p-4 bg-black/30">
        <div className="flex items-center justify-between mb-2">
          <button onClick={onBack} className="text-white/60 text-sm">
            ← Back
          </button>
          <h2 className="text-white font-bold">Release Preparation</h2>
          <div className="text-white/60 text-sm">
            Checked: {checkedLocations.size}/{locations.length}
          </div>
        </div>
        <p className="text-teal-200 text-sm text-center">
          Find the best release location for {otterName}!
        </p>
      </div>

      {/* Map area */}
      <div className="flex-1 relative p-4">
        <div className="w-full h-full bg-blue-900/30 rounded-2xl border-2 border-teal-700/50 relative overflow-hidden">
          {/* Water texture */}
          <div className="absolute inset-0 opacity-30">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-full h-0.5 bg-teal-400/50"
                style={{ top: `${i * 5}%` }}
                animate={{ x: ['-5%', '5%', '-5%'] }}
                transition={{ duration: 3 + i * 0.2, repeat: Infinity }}
              />
            ))}
          </div>

          {/* Location markers */}
          {locations.map(location => {
            const isSelected = selectedLocation?.id === location.id;
            const isChecked = checkedLocations.has(location.id);
            const isFinal = finalChoice?.id === location.id;

            return (
              <motion.button
                key={location.id}
                onClick={() => handleLocationClick(location)}
                className={`
                  absolute -translate-x-1/2 -translate-y-1/2
                  flex flex-col items-center
                `}
                style={{ left: `${location.x}%`, top: `${location.y}%` }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center
                    ${isFinal
                      ? location.suitable
                        ? 'bg-green-500 ring-4 ring-green-300'
                        : 'bg-red-500 ring-4 ring-red-300'
                      : isSelected
                        ? 'bg-yellow-500 ring-2 ring-yellow-300'
                        : isChecked
                          ? 'bg-teal-600'
                          : 'bg-teal-800 hover:bg-teal-700'
                    }
                  `}
                  animate={{
                    scale: isSelected ? 1.2 : 1,
                  }}
                >
                  <MapPin className={`w-5 h-5 ${isSelected ? 'text-yellow-900' : 'text-white'}`} />
                </motion.div>
                <span className={`
                  text-xs mt-1 px-2 py-0.5 rounded-full
                  ${isSelected ? 'bg-yellow-500 text-yellow-900' : 'bg-black/50 text-white'}
                `}>
                  {location.name}
                </span>
              </motion.button>
            );
          })}

          {/* Otter icon */}
          <motion.div
            className="absolute text-4xl"
            style={{ left: '50%', bottom: '10%', transform: 'translateX(-50%)' }}
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            🦦
          </motion.div>
        </div>
      </div>

      {/* Location details panel */}
      <AnimatePresence mode="wait">
        {selectedLocation && !isComplete && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="bg-black/50 rounded-t-2xl p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-bold text-lg">{selectedLocation.name}</h3>
                <p className="text-white/60 text-sm">
                  Overall Score: <span className="text-yellow-400 font-bold">
                    {getOverallScore(selectedLocation)}%
                  </span>
                </p>
              </div>
              <button
                onClick={() => setSelectedLocation(null)}
                className="text-white/60 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2 mb-4">
              <StatBar
                label="Water Quality"
                value={selectedLocation.waterQuality}
                color={selectedLocation.waterQuality >= 70 ? 'bg-green-500' : 'bg-red-500'}
              />
              <StatBar
                label="Food Supply"
                value={selectedLocation.foodAvailability}
                color={selectedLocation.foodAvailability >= 70 ? 'bg-green-500' : 'bg-red-500'}
              />
              <StatBar
                label="Predator Risk"
                value={selectedLocation.predatorRisk}
                color={selectedLocation.predatorRisk <= 30 ? 'bg-green-500' : 'bg-red-500'}
              />
              <StatBar
                label="Human Activity"
                value={selectedLocation.humanActivity}
                color={selectedLocation.humanActivity <= 30 ? 'bg-green-500' : 'bg-red-500'}
              />
            </div>

            <Button
              onClick={handleConfirmRelease}
              className="w-full"
              variant={getOverallScore(selectedLocation) >= 75 ? 'success' : 'primary'}
            >
              Release {otterName} Here
            </Button>

            <p className="text-white/40 text-xs text-center mt-2">
              Check multiple locations before deciding!
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completion overlay */}
      <AnimatePresence>
        {isComplete && finalChoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black/70 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className={`rounded-2xl p-6 text-center ${
                finalChoice.suitable ? 'bg-green-700' : 'bg-red-700'
              }`}
            >
              {finalChoice.suitable ? (
                <>
                  <Check className="w-16 h-16 text-white mx-auto mb-3" />
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Perfect Choice!
                  </h3>
                  <p className="text-white/80 mb-3">
                    {finalChoice.name} is an excellent habitat for {otterName}!
                    Clean water, plenty of food, and minimal threats.
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle className="w-16 h-16 text-white mx-auto mb-3" />
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Not Ideal
                  </h3>
                  <p className="text-white/80 mb-3">
                    {finalChoice.name} has some concerns.
                    {finalChoice.waterQuality < 70 && ' Water quality is poor.'}
                    {finalChoice.foodAvailability < 70 && ' Food is scarce.'}
                    {finalChoice.predatorRisk > 30 && ' Predator risk is high.'}
                    {finalChoice.humanActivity > 30 && ' Too much human activity.'}
                  </p>
                </>
              )}

              <div className="flex justify-center gap-1">
                {[1, 2, 3].map(i => (
                  <Star
                    key={i}
                    className={`w-8 h-8 ${
                      finalChoice.suitable
                        ? (checkedLocations.size >= 4 && i <= 3) ||
                          (checkedLocations.size >= 2 && i <= 2) ||
                          i === 1
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-400'
                        : 'text-gray-400'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Initial instructions */}
      {!selectedLocation && !isComplete && (
        <div className="p-4 bg-black/30">
          <p className="text-white/50 text-xs text-center">
            Tap location markers to check their suitability for release
          </p>
        </div>
      )}
    </div>
  );
}
